/**
 * Sign-in, registration, and everything that changes a credential.
 *
 * Firebase is gone from this app entirely. It was only ever used here for
 * authentication, and the Go API signs its own tokens with Ed25519 (ADR-0007),
 * so there is nothing left for it to do on the web client.
 *
 * A consequence worth knowing: the API's `resend-verification` and
 * `forgot-password` endpoints answer identically whether or not the address is
 * registered, so they cannot be used to enumerate accounts. The UI must not
 * "helpfully" distinguish the two either.
 */

import { api } from "../api";
import type {
    AuthResponse,
    ChangePasswordRequest,
    Identity,
    LoginRequest,
    OAuthSignInRequest,
    RegisterRequest,
    ResetPasswordRequest,
    TokenPair,
    VerifyEmailRequest,
} from "../types";
import {
    sessionCleared,
    sessionEstablished,
} from "../../store/slices/authSlice";

export const authApi = api.injectEndpoints({
    endpoints: (build) => ({
        register: build.mutation<AuthResponse, RegisterRequest>({
            query: (body) => ({ url: "auth/register", method: "POST", body }),
            async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    dispatch(sessionEstablished(data));
                } catch {
                    // Reported to the caller through the hook's error state.
                }
            },
        }),

        login: build.mutation<AuthResponse, LoginRequest>({
            query: (body) => ({ url: "auth/login", method: "POST", body }),
            async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    dispatch(sessionEstablished(data));
                } catch {
                    // Reported to the caller through the hook's error state.
                }
            },
        }),

        /** An id token from a native provider SDK. `provider` is "google" or "apple". */
        oauthSignIn: build.mutation<
            AuthResponse,
            OAuthSignInRequest & { provider: string }
        >({
            query: ({ provider, ...body }) => ({
                url: `auth/oauth/${provider}`,
                method: "POST",
                body,
            }),
            async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    dispatch(sessionEstablished(data));
                } catch {
                    // Reported to the caller through the hook's error state.
                }
            },
        }),

        /**
         * Revokes a refresh token server-side.
         *
         * The local session is cleared whatever the server says: a network
         * failure must not leave someone still signed in on a shared machine
         * after they pressed sign out.
         */
        logout: build.mutation<null, { refreshToken: string }>({
            query: (body) => ({ url: "auth/logout", method: "POST", body }),
            async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;
                } finally {
                    dispatch(sessionCleared());
                    dispatch(api.util.resetApiState());
                }
            },
        }),

        refresh: build.mutation<TokenPair, { refreshToken: string }>({
            query: (body) => ({ url: "auth/refresh", method: "POST", body }),
        }),

        verifyEmail: build.mutation<null, VerifyEmailRequest>({
            query: (body) => ({ url: "auth/verify-email", method: "POST", body }),
            invalidatesTags: ["User"],
        }),

        resendVerification: build.mutation<null, { email: string }>({
            query: (body) => ({
                url: "auth/resend-verification",
                method: "POST",
                body,
            }),
        }),

        /**
         * Ask for a verification code by text.
         *
         * `phoneNumber` is optional: omitted means "the number already on my
         * account", supplied means set it and prove it in one round trip,
         * which is what the profile screen needs when somebody changes theirs.
         *
         * Invalidates User because a successful verify changes phoneVerified
         * and phoneNumber on the profile the rest of the app reads.
         */
        sendPhoneCode: build.mutation<null, { phoneNumber?: string }>({
            query: (body) => ({
                url: "auth/phone/send-code",
                method: "POST",
                body,
            }),
        }),

        verifyPhone: build.mutation<null, { code: string }>({
            query: (body) => ({
                url: "auth/phone/verify",
                method: "POST",
                body,
            }),
            invalidatesTags: ["User"],
        }),

        forgotPassword: build.mutation<null, { email: string }>({
            query: (body) => ({
                url: "auth/forgot-password",
                method: "POST",
                body,
            }),
        }),

        resetPassword: build.mutation<null, ResetPasswordRequest>({
            query: (body) => ({
                url: "auth/reset-password",
                method: "POST",
                body,
            }),
        }),

        changePassword: build.mutation<null, ChangePasswordRequest>({
            query: (body) => ({
                url: "auth/change-password",
                method: "POST",
                body,
            }),
        }),

        /** Federated sign-ins linked to this account. */
        identities: build.query<Identity[], void>({
            query: () => "users/me/identities",
            providesTags: ["Identity"],
        }),

        unlinkIdentity: build.mutation<null, { provider: string }>({
            query: ({ provider }) => ({
                url: `users/me/identities/${provider}`,
                method: "DELETE",
            }),
            invalidatesTags: ["Identity"],
        }),
    }),
});

export const {
    useSendPhoneCodeMutation,
    useVerifyPhoneMutation,
    useRegisterMutation,
    useLoginMutation,
    useOauthSignInMutation,
    useLogoutMutation,
    useRefreshMutation,
    useVerifyEmailMutation,
    useResendVerificationMutation,
    useForgotPasswordMutation,
    useResetPasswordMutation,
    useChangePasswordMutation,
    useIdentitiesQuery,
    useUnlinkIdentityMutation,
} = authApi;
